import React, { useState, useContext } from "react";
import { PlusIcon, Store } from "lucide-react";
import MainLayout from "../components/MainLayout";
import AddRestaurantModal from "../components/NewRestaurant";
import RestaurantPreview from "../components/RestaurantPreview";
import BusinessTable from "../components/SharesDataTable";
import { useQuery } from "@apollo/client";
import { GET_ACTIVE_RESTAURANTS } from "../apollo/Queries";
import { AuthContext } from "../context";

export default function SharesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [previewRestaurant, setPreviewRestaurant] = useState(null);

  const { authenticatedUser } = useContext(AuthContext);
  const isSuperAdmin = authenticatedUser?.userType === "SUPER_ADMIN";

  const { data, loading } = useQuery(GET_ACTIVE_RESTAURANTS, {
    variables: {
      name: "",
      myRestaurants: isSuperAdmin
    },
    fetchPolicy: "cache-and-network"
  })

  const restaurants = data?.getActiveRestaurants?.content;

  const handleEdit = (restaurant) => {
    setEditingRestaurant(restaurant);
    setIsModalOpen(true);
  };

  const handlePreview = (restaurant) => {
    setPreviewRestaurant(restaurant);
    setIsPreviewOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRestaurant(null);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewRestaurant(null);
  };

  return (
    <MainLayout page="Restaurants">
      <div className="space-y-8 animate-fade-in-up">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-white p-8 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.02)] border border-gray-100">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-premium-gold/80">Restaurant Management</span>
            </div>
            <h2 className="text-3xl font-black text-premium-teal tracking-tighter">List of Restaurants</h2>
            <p className="text-sm text-gray-400 font-light">Manage and monitor the collection of partnered restaurants.</p>
          </div>

          <button
            className="premium-button flex items-center gap-3 bg-premium-teal text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-premium-teal/20 hover:scale-105 transition-all"
            onClick={() => {
              setEditingRestaurant(null);
              setIsModalOpen(true);
            }}
          >
            <PlusIcon size={18} className="text-premium-gold" />
            Add New Restaurant
          </button>
        </div>

        {/* Content Section */}
        <div className="bg-white rounded-[2.5rem] shadow-[0_20px_60px_rgba(0,0,0,0.03)] border border-gray-100 p-1 overflow-hidden relative group">
          {loading && !data ? (
            <div className="flex flex-col justify-center items-center h-64 gap-6">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl border-4 border-premium-gold/20 border-t-premium-gold animate-spin"></div>
              </div>
              <p className="text-[10px] font-black text-premium-gold uppercase tracking-[0.5em] animate-pulse">Syncing Hubs...</p>
            </div>
          ) : restaurants && Array.isArray(restaurants) && restaurants.length > 0 ? (
            <div className="p-8">
              <BusinessTable
                businesses={restaurants}
                onEdit={handleEdit}
                onPreview={handlePreview}
              />
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
                <Store size={32} className="text-gray-200" />
              </div>
              <p className="text-gray-400 font-light italic">No restaurants have been materialized in this sector.</p>
            </div>
          )}
        </div>
      </div>

      <AddRestaurantModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        initialData={editingRestaurant}
      />

      <RestaurantPreview
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        restaurant={previewRestaurant}
      />
    </MainLayout>
  );
}


